import React from 'react';
import { Helmet } from 'react-helmet-async';
import { COMPANY } from '../mock/data';

const SITE_URL = 'https://www.frachigers-handwerk.ch';
const DEFAULT_OG = `${SITE_URL}/og-image.jpg`;

/**
 * SEO — per-page meta tags via react-helmet-async.
 * Props:
 *  - title: page title (will be suffixed with brand)
 *  - description: meta description
 *  - path: relative URL path (e.g. "/dienstleistungen/kundenmaurer")
 *  - image: optional OG image absolute URL
 *  - lang: current language (de/en/it/fr)
 *  - noindex: boolean
 *  - jsonLd: array of structured-data objects to inject
 */
const SEO = ({ title, description, path = '/', image = DEFAULT_OG, lang = 'de', noindex = false, jsonLd = [] }) => {
  const fullTitle = title ? `${title} | Frachigers Handwerk · Seelisberg` : 'Frachigers Handwerk · Kundenmaurer & Plattenleger in Seelisberg (UR)';
  const desc = description || 'Frachigers Handwerk – Armin Achermann. Kundenmaurer, Plattenleger, HardRock fugenlose Oberflächen und Ab-Hof-Verkauf in Seelisberg, Kanton Uri. AA+ Qualität, persönlich und zuverlässig.';
  const canonical = `${SITE_URL}${path}`;

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'} />
      <meta name="author" content="Frachigers Handwerk – Armin Achermann" />
      <meta name="geo.region" content="CH-UR" />
      <meta name="geo.placename" content="Seelisberg" />
      <meta name="geo.position" content="46.9714;8.5872" />
      <meta name="ICBM" content="46.9714, 8.5872" />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Frachigers Handwerk" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={lang === 'de' ? 'de_CH' : lang === 'fr' ? 'fr_CH' : lang === 'it' ? 'it_CH' : 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {/* Hreflang for multi-lang */}
      <link rel="alternate" hrefLang="de-CH" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {jsonLd.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

// Reusable JSON-LD builders
export const localBusinessLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/#business`,
  name: 'Frachigers Handwerk',
  legalName: 'Frachigers Handwerk – Armin Achermann',
  description: 'Kundenmaurer, Plattenleger, HardRock fugenlose Oberflächen und Ab-Hof-Verkauf in Seelisberg (Uri). Persönlich, zuverlässig, mit AA+ Qualität seit über 20 Jahren.',
  url: SITE_URL,
  telephone: '+41797194475',
  email: COMPANY.email,
  image: COMPANY.logo,
  logo: COMPANY.logo,
  priceRange: '$$',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Steckenmattstrasse 11',
    addressLocality: 'Seelisberg',
    postalCode: '6377',
    addressRegion: 'UR',
    addressCountry: 'CH',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 46.9714,
    longitude: 8.5872,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '07:00',
      closes: '18:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: 'Saturday',
      opens: '09:00',
      closes: '12:00',
      description: 'Nach Vereinbarung',
    },
  ],
  areaServed: [
    { '@type': 'AdministrativeArea', name: 'Seelisberg' },
    { '@type': 'AdministrativeArea', name: 'Kanton Uri' },
    { '@type': 'AdministrativeArea', name: 'Zentralschweiz' },
  ],
  founder: { '@type': 'Person', name: 'Armin Achermann' },
  slogan: 'AA+ Qualität – Kundenmaurer & Plattenleger',
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Dienstleistungen Frachigers Handwerk',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Kundenmaurer – Maurerarbeiten & Bruchsteinmauern' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Plattenleger – Platten, Naturstein, Fliesen' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'HardRock – Fugenlose Oberflächen' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Ab Hof – Schweizer BIO Rindfleisch' } },
    ],
  },
  sameAs: [
    'https://www.instagram.com/frachigershandwerk/',
  ],
});

export const serviceLd = ({ name, description, image, slug }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  description,
  image,
  url: `${SITE_URL}/dienstleistungen/${slug}`,
  provider: { '@id': `${SITE_URL}/#business` },
  areaServed: [
    { '@type': 'AdministrativeArea', name: 'Seelisberg' },
    { '@type': 'AdministrativeArea', name: 'Kanton Uri' },
    { '@type': 'AdministrativeArea', name: 'Zentralschweiz' },
  ],
  serviceType: name,
});

export const breadcrumbLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export default SEO;
